import { useState, useEffect } from 'react';
import { userStore } from '../../lib/userStore';
import { recordOperationalEvent } from '../../lib/tracker';
import type { UserProfile, UserRole } from '../../types';
import { validateUserCreationPayload, ROLE_DEFAULT_DISCOUNT_LIMITS, ROLE_CAN_APPROVE_DISCOUNTS } from '../../lib/userValidation';

interface AdminUsersProps {
  initialOpenAddModal?: boolean;
}

export default function AdminUsers({ initialOpenAddModal = false }: AdminUsersProps) {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // New User Modal State
  const [showAddModal, setShowAddModal] = useState(initialOpenAddModal);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState<UserRole>('sales_rep');
  const [newPassword, setNewPassword] = useState('');
  const [canApproveDiscounts, setCanApproveDiscounts] = useState(false);
  const [maxDiscount, setMaxDiscount] = useState<number>(10);
  const [modalError, setModalError] = useState('');
  const [modalSuccess, setModalSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Edit User State
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [editRole, setEditRole] = useState<UserRole>('sales_rep');
  const [editName, setEditName] = useState('');
  const [editDiscountLimit, setEditDiscountLimit] = useState<number>(10);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const data = await userStore.getUsers();
      setUsers(data || []);
    } catch (err: any) {
      console.warn('Could not fetch user profiles:', err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoleChange = (role: UserRole) => {
    setNewRole(role);
    setCanApproveDiscounts(ROLE_CAN_APPROVE_DISCOUNTS[role] ?? false);
    setMaxDiscount(ROLE_DEFAULT_DISCOUNT_LIMITS[role] ?? 10);
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setModalError('');
    setModalSuccess('');
    setIsSubmitting(true);

    // Validate payload
    const validation = validateUserCreationPayload({
      name: newName,
      email: newEmail,
      password: newPassword,
      role: newRole,
      max_discount_limit: maxDiscount,
    });

    if (!validation.isValid) {
      setModalError(Object.values(validation.errors)[0]);
      setIsSubmitting(false);
      return;
    }

    try {
      const trimmedEmail = newEmail.trim().toLowerCase();
      const trimmedName = newName.trim();

      await userStore.createUser({
        name: trimmedName,
        email: trimmedEmail,
        password: newPassword,
        role: newRole,
        max_discount_limit: maxDiscount,
        can_approve_discounts: canApproveDiscounts,
      });

      setModalSuccess(`Successfully created user ${trimmedName} (${trimmedEmail})!`);
      setTimeout(() => {
        setShowAddModal(false);
        setNewEmail('');
        setNewName('');
        setNewPassword('');
        setModalSuccess('');
        fetchUsers();
      }, 1200);
    } catch (err: any) {
      setModalError(err.message || 'Failed to create user. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleUserStatus = async (user: UserProfile) => {
    try {
      const updatedStatus = await userStore.toggleUserStatus(user.id);
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, is_active: updatedStatus } : u));

      recordOperationalEvent({
        user_email: user.email,
        user_name: user.name,
        role: user.role,
        action: updatedStatus ? 'user_activated' : 'user_deactivated',
        category: 'auth',
        details: `${updatedStatus ? 'Activated' : 'Deactivated / Suspended'} user account ${user.email}.`
      });
    } catch (err: any) {
      alert('Could not update user status: ' + err.message);
    }
  };

  const handleOpenEdit = (user: UserProfile) => {
    setEditingUser(user);
    setEditRole(user.role);
    setEditName(user.name || '');
    setEditDiscountLimit(user.max_discount_limit ?? 10);
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;
    setIsSavingEdit(true);
    try {
      const updated = await userStore.updateUser(editingUser.id, {
        name: editName,
        role: editRole,
        max_discount_limit: editDiscountLimit,
        can_approve_discounts: editRole === 'admin' || editRole === 'executive' || editDiscountLimit > 15,
      });

      setUsers(prev => prev.map(u => u.id === editingUser.id ? updated : u));

      recordOperationalEvent({
        user_email: editingUser.email,
        user_name: editName,
        role: editRole,
        action: 'user_updated',
        category: 'auth',
        details: `Updated role to ${editRole} and max discount to ${editDiscountLimit}% for ${editingUser.email}.`
      });

      setEditingUser(null);
    } catch (err: any) {
      alert('Failed to update user: ' + err.message);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      (user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && user.is_active !== false) ||
      (filterStatus === 'inactive' && user.is_active === false);
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'executive': return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'manager': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'accountant': return 'bg-cyan-100 text-cyan-700 border-cyan-200';
      case 'marketing': return 'bg-pink-100 text-pink-700 border-pink-200';
      default: return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <div>
          <h2 className="text-xl font-bold text-slate-900">User Provisioning & Staff Directory</h2>
          <p className="text-xs text-slate-500 mt-1">
            Create new team accounts, configure role permissions, set discount authorization limits, and manage status.
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-sm transition-colors flex items-center gap-2"
        >
          <span>➕</span> Create New User
        </button>
      </div>

      {/* Filter Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="relative w-full sm:w-80">
          <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 text-sm">🔍</span>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Roles</option>
            <option value="admin">Admin</option>
            <option value="executive">Executive</option>
            <option value="manager">Manager</option>
            <option value="sales_rep">Sales Representative</option>
            <option value="accountant">Accountant</option>
            <option value="marketing">Marketing</option>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active Only</option>
            <option value="inactive">Deactivated Only</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto mb-3"></div>
            <p className="text-xs text-slate-500">Loading user records...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-16 text-slate-400 text-xs">
            No users match the search and filter criteria.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase text-[10px] font-bold">
                <tr>
                  <th className="px-5 py-3">Team Member</th>
                  <th className="px-5 py-3">Role</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Max Discount</th>
                  <th className="px-5 py-3">Account Created</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredUsers.map((user) => {
                  const isActive = user.is_active !== false;
                  return (
                    <tr key={user.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-slate-800 text-white font-bold flex items-center justify-center text-xs flex-shrink-0">
                            {user.name ? user.name.slice(0, 2).toUpperCase() : user.email.slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-semibold text-slate-900">{user.name || 'Unnamed User'}</div>
                            <div className="text-[11px] text-slate-400 font-mono">{user.email}</div>
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-3.5">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${getRoleBadge(user.role)}`}>
                          {user.role.toUpperCase()}
                        </span>
                      </td>

                      <td className="px-5 py-3.5">
                        <button
                          onClick={() => toggleUserStatus(user)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold transition-colors ${
                            isActive
                              ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                              : 'bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200'
                          }`}
                          title="Click to toggle account access"
                        >
                          <span className={`h-1.5 w-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-rose-500'}`}></span>
                          {isActive ? 'Active' : 'Suspended'}
                        </button>
                      </td>

                      <td className="px-5 py-3.5">
                        <span className="font-mono font-semibold text-slate-700">
                          {user.max_discount_limit != null ? `${user.max_discount_limit}%` : 'Standard'}
                        </span>
                      </td>

                      <td className="px-5 py-3.5 text-slate-500 text-[11px]">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString('en-IN') : 'Pre-existing'}
                      </td>

                      <td className="px-5 py-3.5 text-right space-x-2">
                        <button
                          onClick={() => handleOpenEdit(user)}
                          className="px-2.5 py-1 text-xs font-semibold rounded bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* CREATE USER MODAL */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-100 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                <span>➕</span> Create New User
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-slate-400 hover:text-slate-600 text-lg leading-none"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateUser} className="mt-4 space-y-4 text-xs">
              {modalError && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg border border-red-200">
                  {modalError}
                </div>
              )}
              {modalSuccess && (
                <div className="p-3 bg-emerald-50 text-emerald-700 rounded-lg border border-emerald-200">
                  {modalSuccess}
                </div>
              )}

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  required
                  data-testid="new-user-name-input"
                  placeholder="e.g. Ramesh Sharma"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Corporate Email Address *</label>
                <input
                  type="email"
                  required
                  data-testid="new-user-email-input"
                  placeholder="e.g. ramesh@saikrishnaindustries.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Password *</label>
                <input
                  type="password"
                  required
                  data-testid="new-user-password-input"
                  placeholder="At least 6 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Assigned Role *</label>
                <select
                  data-testid="new-user-role-select"
                  value={newRole}
                  onChange={(e) => handleRoleChange(e.target.value as UserRole)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="sales_rep">Sales Representative (Create Quotations)</option>
                  <option value="manager">Branch / Sales Manager (Review & Approve)</option>
                  <option value="executive">Director / Executive (Full View)</option>
                  <option value="admin">System Administrator (Full /admin Access)</option>
                  <option value="accountant">Accounts & Billing</option>
                  <option value="marketing">Digital Marketing Team</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-1">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Max Discount Limit (%)</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    data-testid="new-user-discount-input"
                    value={maxDiscount}
                    onChange={(e) => setMaxDiscount(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex flex-col justify-center pt-4">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid="new-user-approve-checkbox"
                      checked={canApproveDiscounts}
                      onChange={(e) => setCanApproveDiscounts(e.target.checked)}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="font-semibold text-slate-700 text-[11px]">Can Approve Overrides</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="new-user-submit-btn"
                  disabled={isSubmitting}
                  className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-sm disabled:opacity-50"
                >
                  {isSubmitting ? 'Creating...' : 'Create Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT USER MODAL */}
      {editingUser && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-100">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h3 className="font-bold text-slate-900 text-base">Edit User: {editingUser.email}</h3>
              <button onClick={() => setEditingUser(null)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>

            <div className="mt-4 space-y-4 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Full Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Assigned Role</label>
                <select
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value as UserRole)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="sales_rep">Sales Representative</option>
                  <option value="manager">Branch / Sales Manager</option>
                  <option value="executive">Director / Executive</option>
                  <option value="admin">System Administrator (/admin access)</option>
                  <option value="accountant">Accounts & Billing</option>
                  <option value="marketing">Digital Marketing Team</option>
                </select>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Max Discount Limit (%)</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={editDiscountLimit}
                  onChange={(e) => setEditDiscountLimit(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isSavingEdit}
                  onClick={handleSaveEdit}
                  className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-50"
                >
                  {isSavingEdit ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
